import React, { useState } from 'react';
import { styled } from 'styled-components';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { strings } from '/imports/strings';
import { Page } from '/imports/ui/Page';
import { useFindOne, useMethod, useSubscribe, useIsLuminia } from '/imports/hooks';
import { sanDiegoCoords, useGoogleMap } from '/imports/gmaps';
import { PageHeading } from '/imports/ui/PageHeading';
import { PageBody } from '/imports/ui/PageBody';
import { GoogleMap, Marker } from '@react-google-maps/api';
import { Button } from '/imports/ui/Button';
import { DividerTabs } from '/imports/ui/DividerTabs';
import { LoadingFlex } from '/imports/ui/LoadingFlex';
import { Icon } from '/imports/ui/Icon';
import { NotFoundPage } from '/imports/ui/NotFoundPage';
import { Publications } from '/imports/publications';
import { toastOnError } from '/imports/ui/utils';
import { Loading } from '/imports/ui/Loading';
import { css } from '/imports/ui/css';
import { Input } from '/imports/ui/Input';
import { setOrgId } from '/imports/api/users';
import { EnrichmentsSection, ProjectDetails } from '/imports/ui/ProjectViewDetails';
import { DataRoomSection } from '/imports/ui/ProjectViewDataRoom';
import { QuotesList, QuotesSection } from '/imports/ui/ProjectViewQuotes';
import { AgreementsSection } from '/imports/ui/ProjectViewAgreements';
import { ProjectViewTimeline } from '/imports/ui/ProjectViewTimeline';
import { RealtimeUpdates } from '/imports/api/realtimeUpdates';
import {
  Project, Projects, enrichProject, modelClass, projectStatus, validateProjectForQuote
} from '/imports/api/projects';

const ProjectViewPageStyled = styled(Page)`
  .page-heading {
    position: sticky;
    top: 0;
    background-color: ${css.white};
    z-index: 1;
    .btn {
      min-width: 6rem;
      &.enriching {
        display: none;
      }
      &.cancel {
        background-color: ${css.red1};
        &:hover {
          background-color: ${css.red2};
        }
      }
    }
    .enrichment-message {
      font-size: 1rem;
      font-weight: normal;
    }
  }
  .page-body {
    .flex-wrapper {
      
    }
    .map-section {
      margin-top: 2rem;
    }
    .change-image-input {
      display: none;
    }
    h3 {
      color: ${css.primaryColor};
    }
    .notes {
      width: 100%;
      label {
        font-weight: bold;
      }
      textarea {
        width: calc(100% - 2rem);
        height: 9rem;
        &:disabled {
          background: ${css.grey9};
        }
      }
    }
  }
  .main-buttons {
    margin-top: 1rem;
    display: flex;
    gap: 1rem;
  }
  .loading-flex {
    position: absolute;
    width: 100%;
    height: 100%;
    opacity: 0.75;
  }
  .name-input {
    input {
      font-size: 1.75rem;
    }
    .icon {
      position: relative;
      left: -2.5rem;
    }
  }
  .project-timeline {
    margin-left: 1rem;
  }
`;

/** Project detail page: header, editable fields, map, and tabbed sections (quotes, dataroom, etc.). */
export const ProjectViewPage = () => {
  const isLuminia = useIsLuminia();
  const { _id } = useParams() as { _id: string };
  const { isLoaded } = useGoogleMap();
  const [update, setUpdate] = useState<Partial<Project>>({ _id });
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<any>(null);
  const [activeTab, setActiveTab] = useState('quotes');
  const [_project, _loadingProject] = useMethod(modelClass.getMethod, _id, [_id]);
  const loadingProject = useSubscribe(Publications.getProject, _id);
  const __project = useFindOne(() => Projects.find(_id), [_id, loadingProject()]);
  const project = __project || _project;
  useSubscribe(Publications.realtimeUpdate, { docId: _id, type: 'enrichment' });
  const realtimeUpdate = useFindOne(() => RealtimeUpdates.find({ docId: _id, type: 'enrichment' }));
  /**
   * Validates and persists project edits, optionally triggering enrichment and org switch.
   * @param _update - fields to save; falls back to current edit state
   * @param enrich - whether to re-run enrichment after saving
   */
  const save = async (_update?: Partial<Project>, enrich = true) => {
    const __update = _update || update;
    if (__update.name !== undefined && __update.name.trim() === '') {
      toast.error(strings.ProjectNameIsRequired());
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await modelClass.updateMethod.call({ ...__update, _enrich: enrich });
      setEditing(false);
      if (__update.orgId && __update.orgId !== project.orgId) {
        await setOrgId.call(__update.orgId);
        window.location.reload();
      }
    }
    catch (e: any) {
      if (e.error === 'validationError' && e.details) setError(JSON.parse(e.details));
      else toast.error(e.message || e.reason || `${e}`);
    }
    finally {
      setSaving(false);
    }
  };
  /** Forces a fresh enrichment run for this project. */
  const enrich = toastOnError(async () => {
    await enrichProject.call({ _id, force: true });
    toast.success(strings.Running());
  });
  /** Checks whether the project has everything needed for an ESS+solar quote, surfacing any errors. */
  const validate = toastOnError(async () => {
    const errors = await validateProjectForQuote.call({ projectId: _id, proposalType: 'essSolar' });
    if (errors.length) return toast.error(<div>{errors.map((e) => <div key={e} style={{ marginBottom: '0.5rem' }}>{e}</div>)}</div>, { autoClose: 10000, style: { width: '35rem' } });
    toast.success(strings.Ok());
  });
  const address = update.address || project?.address;
  if (loadingProject() || _loadingProject || !isLoaded) return <LoadingFlex />;
  if (!project) return <NotFoundPage />;
  return (
    <ProjectViewPageStyled>
      <PageHeading
        left={
          <h1>
            <Link to="/projects"><Icon icon="chevronLeft" /></Link>
            {editing ? <Input id="proj-name" className="name-input" value={update.name !== undefined ? update.name : project.name} onChange={(name) => setUpdate({ ...update, name })} /> : project?.name}
            {project.enrichmentStatus !== 'done' ? <><Loading /><span className="enrichment-message">{realtimeUpdate?.message || ''}</span></> : null}
          </h1>
        }
        right={
          <span>
            {
              isLuminia && !editing ?
                <Button id="enrich-btn" onClick={() => enrich()}>{strings.Enrich()}</Button>
                :
                null
            }
            {
              isLuminia ?
                <Button id="validate-btn" onClick={() => validate()}>{strings.Validate()}</Button>
                :
                null
            }
            {
              editing ?
                <>
                  <Button id="save-btn" className={`btn primary${project.enrichmentStatus !== 'done' ? ' enriching' : ''}`} onClick={() => save(undefined, true)}>{saving ? <Loading /> : `${strings.Save()}+${strings.Enrich()}`}</Button>
                  <Button id="save-btn" className={`btn primary${project.enrichmentStatus !== 'done' ? ' enriching' : ''}`} onClick={() => save(undefined, false)}>{saving ? <Loading /> : strings.Save()}</Button>
                  <Button id="cancel-btn" className={'btn cancel primary'} onClick={() => { setUpdate({ _id }); setError(null); setEditing(false); }}>{strings.Cancel()}</Button>
                </>
                :
                <Button id="edit-button" className={`btn primary${project.enrichmentStatus !== 'done' ? ' enriching' : ''}`} onClick={() => setEditing(true)}>{strings.Edit()}</Button>
            }
          </span>
        }
      />
      <PageBody>
        <ProjectDetails project={project} update={update} setUpdate={setUpdate} editing={editing} error={error} />
        <div className="infoblock">
          <hr />
          <div className="label">{strings.Status()}</div>
          <div className="value">{projectStatus(project)}</div>
          <hr />
        </div>
        <DividerTabs
          activeTab={activeTab}
          onTabChange={setActiveTab}
          tabs={[
            { id: 'quotes', label: strings.Quotes() },
            { id: 'dataroom', label: strings.Dataroom() },
            { id: 'agreements', label: strings.Agreements() },
            { id: 'admin', label: strings.Admin() },
            ...(isLuminia ? [{ id: 'timeline', label: strings.Timeline() }, { id: 'enrichment', label: strings.EnrichmentDetails() }] : []),
          ]}
        />
        {
          activeTab === 'quotes' ?
            <>
              <QuotesSection project={project} update={update} setUpdate={setUpdate} editing={editing} save={save} error={error} />
              <QuotesList project={project} />
            </>
            :
            null
        }
        {
          activeTab === 'dataroom' ?
            <DataRoomSection project={project} />
            :
            null
        }
        {
          activeTab === 'agreements' ?
            <AgreementsSection project={project} update={update} setUpdate={setUpdate} editing={editing} />
            :
            null
        }
        {
          activeTab === 'timeline' ?
            <ProjectViewTimeline project={project} onNavigate={setActiveTab} />
            :
            null
        }
        {
          activeTab === 'enrichment' ?
            <EnrichmentsSection project={project} />
            :
            null
        }
        <div className="map-section">
          <GoogleMap
            mapContainerStyle={{ width: '100%', minHeight: '35rem' }} center={address?.location || sanDiegoCoords}
            zoom={address?.location ? 19 : 3} mapTypeId="hybrid"
          >
            {
              address ? <Marker position={address.location} /> : null
            }
          </GoogleMap>
        </div>
      </PageBody>
    </ProjectViewPageStyled>
  );
};
